/**
 * Image utility functions for resizing and compressing images
 * Used for AI classification and storage optimization
 */

interface ResizeOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  format?: 'image/jpeg' | 'image/png' | 'image/webp';
}

interface ResizedImage {
  blob: Blob;
  base64: string;
  width: number;
  height: number;
  originalSize: number;
  newSize: number;
  compressionRatio: number;
}

/**
 * Resize and compress an image file or blob
 * Returns both blob and base64 for flexibility
 */
export async function resizeImage(
  source: File | Blob | string,
  options: ResizeOptions = {}
): Promise<ResizedImage> {
  const {
    maxWidth = 1024,
    maxHeight = 1024,
    quality = 0.8,
    format = 'image/jpeg'
  } = options;

  // Load the image
  const img = await loadImage(source);
  const originalSize = source instanceof Blob ? source.size : 0;

  // Calculate new dimensions maintaining aspect ratio
  let { width, height } = img;
  
  if (width > maxWidth || height > maxHeight) {
    const ratio = Math.min(maxWidth / width, maxHeight / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  // Create canvas and draw resized image
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  // Use better quality interpolation
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, width, height);

  // Convert to blob
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error('Failed to create blob'));
      },
      format,
      quality
    );
  });

  // Convert to base64
  const base64 = await blobToBase64(blob);

  return {
    blob,
    base64,
    width,
    height,
    originalSize,
    newSize: blob.size,
    compressionRatio: originalSize > 0 ? (1 - blob.size / originalSize) * 100 : 0
  };
}

/**
 * Resize image specifically for AI classification
 * Uses smaller dimensions and lower quality since AI doesn't need high resolution
 */
export async function resizeForClassification(
  source: File | Blob | string
): Promise<{ base64: string; compressionInfo: string }> {
  const result = await resizeImage(source, {
    maxWidth: 512,
    maxHeight: 512,
    quality: 0.7,
    format: 'image/jpeg'
  });

  const compressionInfo = `Resized to ${result.width}x${result.height}, ${formatBytes(result.newSize)} (${result.compressionRatio.toFixed(0)}% smaller)`;
  
  return {
    base64: result.base64,
    compressionInfo
  };
}

/**
 * Resize image for display/storage
 * Higher quality than classification but still optimized
 */
export async function resizeForDisplay(
  source: File | Blob | string,
  maxDimension: number = 1200
): Promise<ResizedImage> {
  return resizeImage(source, {
    maxWidth: maxDimension,
    maxHeight: maxDimension,
    quality: 0.85,
    format: 'image/jpeg'
  });
}

/**
 * Create a thumbnail version of an image
 */
export async function createThumbnail(
  source: File | Blob | string,
  size: number = 200
): Promise<ResizedImage> {
  return resizeImage(source, {
    maxWidth: size,
    maxHeight: size,
    quality: 0.7,
    format: 'image/jpeg'
  });
}

/**
 * Load an image from various sources
 */
function loadImage(source: File | Blob | string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      // Clean up object URL if we created one
      if (source instanceof Blob) {
        URL.revokeObjectURL(img.src);
      }
      resolve(img);
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    
    if (source instanceof Blob) {
      img.src = URL.createObjectURL(source);
    } else {
      img.src = source;
    }
  });
}

/**
 * Convert a blob to base64 string (without data URL prefix for API calls)
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Format bytes to human readable string
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Extract base64 from a data URL
 */
export function extractBase64FromDataUrl(dataUrl: string): string {
  const parts = dataUrl.split(',');
  return parts.length > 1 ? parts[1] : dataUrl;
}

/**
 * Check if an image needs resizing based on file size
 */
export function needsResizing(file: File | Blob, maxSizeBytes: number = 1024 * 1024): boolean {
  return file.size > maxSizeBytes;
}
